import { useState } from "react";
import { Copy, Check } from "lucide-react";

/**
 * Copy-link + LinkedIn/X share icons for a story. Split out of
 * InsightDetailPage.tsx to keep that page under the 400-line limit. Identical
 * behavior as before — URL preference is the canonical per-story URL, falling
 * back to the legacy `#story-N` hash for the pathological empty-slug case.
 */

const ICON_LINKEDIN = (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const ICON_X = (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

interface ShareBarProps {
  date: string;
  slug: string;
  index: number;
  headline: string;
}

// Always the public canonical origin. Deriving it from window.location.origin
// breaks during SSR (undefined → "http://localhost/..."), baking localhost
// share/copy URLs into the crawled HTML. The shared target is always canonical.
const SITE_ORIGIN = "https://altair-health.com";

export function ShareBar({ date, slug, index, headline }: ShareBarProps) {
  const [copied, setCopied] = useState(false);
  const url = slug
    ? `${SITE_ORIGIN}/news/${date}/${slug}`
    : `${SITE_ORIGIN}/news/${date}#story-${index}`;

  function handleCopy() {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(headline)}`;

  const iconStyle = {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "hsl(var(--foreground) / 0.25)",
    padding: 4,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    transition: "color 150ms, background 150ms",
    textDecoration: "none",
  } as const;

  function makeHover(hoverColor: string) {
    return {
      onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.style.color = hoverColor;
        e.currentTarget.style.background = "hsl(var(--foreground) / 0.04)";
      },
      onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
        e.currentTarget.style.color = "hsl(var(--foreground) / 0.25)";
        e.currentTarget.style.background = "transparent";
      },
    };
  }

  return (
    <div className="flex items-center gap-1" style={{ marginTop: 16 }}>
      <button
        onClick={handleCopy}
        style={iconStyle}
        {...makeHover("hsl(var(--foreground) / 0.60)")}
        title={copied ? "Copied!" : "Copy link"}
      >
        {copied ? <Check size={15} /> : <Copy size={15} />}
      </button>
      <a
        href={linkedInUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={iconStyle}
        {...makeHover("hsl(210 80% 45%)")}
        title="Share on LinkedIn"
      >
        {ICON_LINKEDIN}
      </a>
      <a
        href={xUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={iconStyle}
        {...makeHover("hsl(var(--foreground))")}
        title="Share on X"
      >
        {ICON_X}
      </a>
    </div>
  );
}
